import React, { useEffect, useState } from 'react';
import { Calendar, Mail, MapPin, Phone } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { offlineCentersAPI } from '../utils/api';

const OfflineCentersPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(slug ? null : []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const request = slug ? offlineCentersAPI.getBySlug(slug) : offlineCentersAPI.getAll();
    request.then(({ data: response }) => setData(response.data))
      .catch(() => setError(slug ? 'This center is unavailable.' : 'Centers could not be loaded.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <main className="min-h-screen bg-black px-4 pt-32 text-center text-gray-300" role="status">Loading centers…</main>;
  if (error) return <main className="min-h-screen bg-black px-4 pt-32"><div role="alert" className="mx-auto max-w-3xl rounded-lg border border-red-700 bg-red-950 p-5 text-red-100">{error}</div></main>;

  if (slug) return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white">
      <div className="mx-auto max-w-5xl">
        <Link to="/offline/centers" className="text-yellow-400 hover:text-yellow-300">← All centers</Link>
        <header className="my-8 rounded-xl border border-zinc-800 bg-zinc-900 p-6 sm:p-8">
          <h1 className="text-3xl font-bold">{data.name}</h1>
          <p className="mt-3 flex items-start gap-2 text-gray-300"><MapPin className="mt-0.5 shrink-0 text-yellow-400" size={20} />{[data.address_line_1, data.address_line_2, data.city, data.state, data.postal_code].filter(Boolean).join(', ')}</p>
          <div className="mt-4 flex flex-wrap gap-5 text-sm text-gray-300">
            {data.phone && <a className="flex items-center gap-2 hover:text-yellow-400" href={`tel:${data.phone}`}><Phone size={17} />{data.phone}</a>}
            {data.email && <a className="flex items-center gap-2 hover:text-yellow-400" href={`mailto:${data.email}`}><Mail size={17} />{data.email}</a>}
          </div>
        </header>
        <section aria-labelledby="sessions-heading">
          <h2 id="sessions-heading" className="mb-5 text-2xl font-bold">Upcoming sessions</h2>
          {data.sessions.length === 0 ? <p className="rounded-xl border border-zinc-800 bg-zinc-900 p-7 text-gray-300">No sessions are currently scheduled at this center.</p> : data.sessions.map((session) => (
            <article key={session.id} className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900 p-6">
              <p className="mb-2 flex items-center gap-2 text-sm text-yellow-400"><Calendar size={17} />{new Date(session.starts_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
              <h3 className="text-xl font-bold">{session.program.title}</h3>
              <p className="my-3 text-gray-400">{session.program.short_description}</p>
              <Link className="font-semibold text-yellow-400 hover:text-yellow-300" to={`/live/${session.program.type}/${session.program.slug}`}>Program details →</Link>
            </article>
          ))}
        </section>
      </div>
    </main>
  );

  return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white">
      <div className="mx-auto max-w-6xl">
        <header className="mb-10 text-center"><h1 className="text-4xl font-bold">Offline <span className="text-yellow-400">Centers</span></h1><p className="mt-3 text-gray-400">Find active classroom locations and upcoming sessions.</p></header>
        {data.length === 0 ? <p className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 text-center text-gray-300">No offline centers are active yet.</p> : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{data.map((center) => (
            <article key={center.id} className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
              <MapPin className="mb-4 text-yellow-400" />
              <h2 className="text-xl font-bold">{center.name}</h2>
              <p className="mt-2 text-gray-400">{center.address_line_1}, {center.city}, {center.state} {center.postal_code}</p>
              <p className="my-4 flex items-center gap-2 text-sm text-gray-300"><Calendar size={17} />{center.upcoming_sessions_count} upcoming sessions</p>
              <Link className="font-semibold text-yellow-400 hover:text-yellow-300" to={`/offline/centers/${center.slug}`}>View center →</Link>
            </article>
          ))}</div>
        )}
      </div>
    </main>
  );
};

export default OfflineCentersPage;
